/**
 * AvailableUnits — the per-unit breakdown for a development (from ?unitdetails=1,
 * mapped to `offer.units`). Shows unit name, typology, beds/baths, surface area,
 * price and an availability status badge.
 *
 * Placement (Figma-style admin table, trimmed to the fields the feed provides):
 *   - Desktop: a card rendered ABOVE the map in the property section.
 *   - Mobile:  a full-width button that opens the table in a dialog.
 *
 * Renders nothing when the offer has no units (non-developments / no breakdown).
 *
 * Props:
 *   - offer {{ units, unit }} — the offer view-model
 */
import React, { useState, useRef, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useStrings } from './i18n';
import { useIsMobile, useMediaQuery } from './hooks';
import { IconBed, IconBath, IconChevronRight, IconChevronLeft, IconX } from './icons';
import Money from './Money';
import { cdnSize } from './mapProperty';

// Desktop table: preview the first 5 units, then "See more" reveals the rest
// paginated at 18 per page. Mobile cards page at >15 (5/page).
const DESKTOP_PREVIEW = 5;
const DESKTOP_PAGE_SIZE = 18;
const CARDS_THRESHOLD = 15;
const CARDS_PAGE_SIZE = 5;

// Compact pager: ‹ prev · "page / total" · next › (numbered buttons don't scale
// to big developments, e.g. 100+ units).
function Pager({ page, pageCount, onPage, t }) {
    return (
        <div className="rs-units__pager">
            <button type="button" className="rs-units__page rs-units__page--nav"
                disabled={page === 0} onClick={() => onPage(page - 1)} aria-label={t.prevPhoto}>
                <IconChevronLeft size={18} />
            </button>
            <span className="rs-units__pageinfo">{page + 1} / {pageCount}</span>
            <button type="button" className="rs-units__page rs-units__page--nav"
                disabled={page === pageCount - 1} onClick={() => onPage(page + 1)} aria-label={t.nextPhoto}>
                <IconChevronRight size={18} />
            </button>
        </div>
    );
}

// Unit availability code (per the feed's UntStatus). Only available units are
// listed, so the other codes (2 hold / 4 sold / 8 reserved) are no longer needed.
const STATUS_UNIT_AVAILABLE = 1;

// Per-unit key-ready flag ("1"/"0") → Yes / No, or an em dash when unknown.
function keyReadyLabel(t, v) {
    if (v === '1' || v === 1) return t.yes;
    if (v === '0' || v === 0) return t.no;
    return '—';
}

// The table itself — shared by the desktop card and the mobile dialog. Wrapped in
// a horizontal-scroll container so the columns never force the page/dialog wide.
function UnitsTable({ units, unit, thumb }) {
    const t = useStrings();
    // Show a right-edge fade + hint while the table can still scroll right; it
    // clears once scrolled to the end (so it never lies about hidden columns).
    const scrollRef = useRef(null);
    const [more, setMore] = useState(false);
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return undefined;
        const check = () => setMore(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
        check();
        el.addEventListener('scroll', check, { passive: true });
        window.addEventListener('resize', check);
        return () => { el.removeEventListener('scroll', check); window.removeEventListener('resize', check); };
    }, [units.length]);
    const scrollToEnd = () => {
        const el = scrollRef.current;
        if (el) el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
    };

    // Desktop: preview the first 5 units; "See more" reveals the rest, paginated
    // at 18 per page. Collapsed by default.
    const [expanded, setExpanded] = useState(false);
    const canExpand = units.length > DESKTOP_PREVIEW;
    const pageCount = expanded ? Math.ceil(units.length / DESKTOP_PAGE_SIZE) : 1;
    const [page, setPage] = useState(0);
    const p = Math.min(page, pageCount - 1); // guard if the list shrank
    let shown;
    if (!expanded) shown = units.slice(0, DESKTOP_PREVIEW);
    else shown = pageCount > 1 ? units.slice(p * DESKTOP_PAGE_SIZE, p * DESKTOP_PAGE_SIZE + DESKTOP_PAGE_SIZE) : units;
    const collapse = () => { setExpanded(false); setPage(0); };

    // Drop the outdoor-space columns entirely when no listed unit has one — an
    // all-dashes column is just noise. Checked across every unit, not just the
    // current page, so the columns don't appear/disappear while paging.
    const hasTerrace = units.some((u) => u.terraceArea > 0);
    const hasGarden = units.some((u) => u.gardenArea > 0);

    return (
        <>
        <div className={`rs-units__scrollwrap ${more ? 'has-more' : ''}`}>
            <div className="rs-units__scroll" ref={scrollRef}>
                <table className="rs-units__table">
                <thead>
                    <tr>
                        <th>{t.unit}</th>
                        <th>{t.type}</th>
                        <th>{t.price}</th>
                        <th>{t.surfaceArea}</th>
                        {hasTerrace && <th>{t.terrace}</th>}
                        {hasGarden && <th>{t.garden}</th>}
                        <th>{t.bedrooms}</th>
                        <th>{t.bathrooms}</th>
                        <th>{t.keyReady}</th>
                    </tr>
                </thead>
                <tbody>
                    {shown.map((u) => (
                        <tr key={u.id}>
                            <td className="rs-units__name">
                                <span className="rs-units__cell">
                                    {thumb ? <img className="rs-units__thumb" src={thumb} alt="" /> : null}
                                    {u.name || '—'}
                                </span>
                            </td>
                            <td>{u.typology || '—'}</td>
                            <td className="rs-units__price"><Money>{u.priceLabel}</Money></td>
                            <td>{u.area > 0 ? `${u.area} ${unit}` : '—'}</td>
                            {hasTerrace && <td>{u.terraceArea > 0 ? `${u.terraceArea} ${unit}` : '—'}</td>}
                            {hasGarden && <td>{u.gardenArea > 0 ? `${u.gardenArea} ${unit}` : '—'}</td>}
                            <td>{u.beds || '—'}</td>
                            <td>{u.baths || '—'}</td>
                            <td>{keyReadyLabel(t, u.keyReady)}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
            <button type="button" className="rs-units__more" onClick={scrollToEnd} aria-label={t.nextPhoto}>
                <IconChevronRight size={22} />
            </button>
        </div>
        {!expanded && canExpand && (
            <button type="button" className="rs-units__toggle" onClick={() => setExpanded(true)}>
                {t.seeMore} ({units.length - DESKTOP_PREVIEW})
            </button>
        )}
        {expanded && (
            <div className="rs-units__foot">
                {pageCount > 1 && <Pager page={p} pageCount={pageCount} onPage={setPage} t={t} />}
                {canExpand && (
                    <button type="button" className="rs-units__toggle" onClick={collapse}>
                        {t.seeLess}
                    </button>
                )}
            </div>
        )}
        </>
    );
}

// Mobile layout — one card per unit. The header row carries the thumbnail, name,
// type and price (no labels); beds / baths / area sit below as icon chips. Floors
// and the status-as-a-row are dropped for the compact mobile view (status shows
// as a badge on the header).
function UnitsCards({ units, unit, thumb }) {
    const t = useStrings();
    // Paginate at > 15 units — 5 per page, or 4 on a short viewport so the pager
    // stays reachable without scrolling.
    const short = useMediaQuery('(max-height: 700px)');
    const pageSize = short ? 4 : CARDS_PAGE_SIZE;
    const pageCount = units.length > CARDS_THRESHOLD ? Math.ceil(units.length / pageSize) : 1;
    const [page, setPage] = useState(0);
    const p = Math.min(page, pageCount - 1);
    const shown = pageCount > 1 ? units.slice(p * pageSize, p * pageSize + pageSize) : units;
    return (
        <>
        <div className="rs-ucards">
            {shown.map((u) => (
                <div className="rs-ucard" key={u.id}>
                    <div className="rs-ucard__top">
                        {thumb ? <img className="rs-ucard__thumb" src={thumb} alt="" /> : null}
                        <div className="rs-ucard__id">
                            <div className="rs-ucard__name">{u.name || '—'}</div>
                            <div className="rs-ucard__type">{u.typology || '—'}</div>
                        </div>
                        <div className="rs-ucard__pricecol">
                            <div className="rs-ucard__price"><Money>{u.priceLabel}</Money></div>
                        </div>
                    </div>
                    <div className="rs-ucard__specs">
                        <span className="rs-ucard__spec"><IconBed size={18} /> {u.beds || '—'}</span>
                        <span className="rs-ucard__spec"><IconBath size={18} /> {u.baths || '—'}</span>
                        <span className="rs-ucard__spec">{t.surfaceArea}: {u.area > 0 ? `${u.area} ${unit}` : '—'}</span>
                        {/* outdoor space only when the unit actually has some */}
                        {u.terraceArea > 0 && (
                            <span className="rs-ucard__spec">{t.terrace}: {u.terraceArea} {unit}</span>
                        )}
                        {u.gardenArea > 0 && (
                            <span className="rs-ucard__spec">{t.garden}: {u.gardenArea} {unit}</span>
                        )}
                        <span className="rs-ucard__spec">{t.keyReady}: {keyReadyLabel(t, u.keyReady)}</span>
                    </div>
                </div>
            ))}
        </div>
        {pageCount > 1 && <Pager page={p} pageCount={pageCount} onPage={setPage} t={t} />}
        </>
    );
}

export default function AvailableUnits({ offer }) {
    const t = useStrings();
    const isMobile = useIsMobile();
    const [open, setOpen] = useState(false);
    // Reactive swipe-to-close for the mobile drawer: the whole dialog tracks a
    // rightward drag and closes if released past the threshold, else snaps back.
    // (Declared before the early return so hook order stays stable.)
    const swipe = useRef({ x: 0, y: 0, active: false, horiz: false, dx: 0, dialog: null });
    const units = offer.units || [];
    if (!units.length) return null;

    // Only available units are listed; the heading still reports the total so the
    // reader can see how much of the development is left (e.g. "(4/20)").
    // The current feed already sends available units only; the filter still covers
    // legacy (unfiltered) array feeds. `totalUnits` is the development's true total.
    const availableUnits = units.filter((u) => u.status === STATUS_UNIT_AVAILABLE);
    const total = offer.totalUnits || units.length;
    const heading = `${t.availableUnits} (${availableUnits.length}/${total})`;
    // Mobile button uses the shorter label so it fits the pill.
    const buttonLabel = `${t.availableUnitsShort} (${availableUnits.length}/${total})`;
    // 36px table / 52px card thumbnails — load the small CDN variant, not the
    // full-size gallery image (thumbnails on media-v3, /w400/ elsewhere).
    const thumb = cdnSize((offer.gallery && offer.gallery[0]) || '', 'thumbnails');

    const onDrawerStart = (e) => {
        const dialog = e.currentTarget.closest('.modal-dialog');
        swipe.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, active: true, horiz: false, dx: 0, dialog };
    };
    const onDrawerMove = (e) => {
        const s = swipe.current;
        if (!s.active || !s.dialog) return;
        const dx = e.touches[0].clientX - s.x;
        const dy = e.touches[0].clientY - s.y;
        if (!s.horiz) {
            if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
            s.horiz = Math.abs(dx) > Math.abs(dy);
            if (!s.horiz) { s.active = false; return; } // vertical → let the list scroll
            s.dialog.style.transition = 'none'; // follow the finger while dragging
        }
        s.dx = Math.max(0, dx); // rightward only
        s.dialog.style.transform = `translateX(${s.dx}px)`;
    };
    const onDrawerEnd = () => {
        const s = swipe.current;
        if (!s.active || !s.dialog) return;
        s.active = false;
        const el = s.dialog;
        el.style.transition = 'transform .25s ease';
        const closeAt = Math.min(120, (el.offsetWidth || 320) * 0.35);
        if (s.dx > closeAt) {
            el.style.transform = 'translateX(100%)';
            setTimeout(() => { el.style.transform = ''; el.style.transition = ''; setOpen(false); }, 180);
        } else {
            el.style.transform = '';
            setTimeout(() => { el.style.transition = ''; }, 260);
        }
    };

    // Mobile: a button that opens the table in a dialog.
    if (isMobile) {
        return (
            <>
                <button className="rs-pill rs-pill--tint rs-units__btn" onClick={() => setOpen(true)}>
                    {buttonLabel}
                </button>
                <Modal show={open} onHide={() => setOpen(false)} dialogClassName="rs-units-modal">
                    <Modal.Header>
                        <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>{t.availableUnits}</Modal.Title>
                        <button type="button" className="rs-units__close" onClick={() => setOpen(false)} aria-label={t.close}>
                            <IconX size={22} />
                        </button>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="rs-proto"
                            onTouchStart={onDrawerStart} onTouchMove={onDrawerMove} onTouchEnd={onDrawerEnd}>
                            <UnitsCards units={availableUnits} unit={offer.unit} thumb={thumb} />
                        </div>
                    </Modal.Body>
                </Modal>
            </>
        );
    }

    // Desktop: a card above the map.
    return (
        <section className="rs-card rs-units">
            <p className="rs-units__h">{heading}</p>
            <UnitsTable units={availableUnits} unit={offer.unit} thumb={thumb} />
        </section>
    );
}
